import Constants from 'expo-constants';
import * as Updates from 'expo-updates';
import { type UpdateFetchResult } from 'expo-updates';
import { Platform } from 'react-native';

import { type Guard } from '../ota-update-state-machine.types';
import { type checkForInitialUpdate } from '../services';

// ─── Guards ──────────────────────────────────────────────────────────────────

/**
 * Checks whether OTA updates are set to be automatic, which means
 * the app will check for a possible update immediately upon opening.
 *
 * - If automatic updates are enabled, this guard prevents extra checks.
 * - If automatic updates are disabled, we can manually check for updates on launch.
 *
 * @see {@link https://docs.expo.dev/versions/latest/config/app/#updates}
 */
const checkIfAutoUpdateEnabled: Guard = () => {
  const updatesConfig = Constants.expoConfig?.updates;

  // NOTE: When the `updates.checkAutomatically` setting is not specified,
  //       Expo will use the 'ON_LOAD' option as the default.
  const isAutoUpdatesConfigNotSet = !updatesConfig?.checkAutomatically;
  const isAutoUpdatesOnLoadEnabled = updatesConfig?.checkAutomatically === 'ON_LOAD';

  return isAutoUpdatesConfigNotSet || isAutoUpdatesOnLoadEnabled;
};

/**
 * Checks whether OTA updates are supported in the current environment.
 *
 * Expo supports OTA updates only in the native published builds, which
 * means that OTA updates are not available on the web platform as well as
 * Expo Go and development clients.
 *
 * NOTE: Expo uses `__DEV__` variable to determine development mode.
 *
 * @see {@link https://docs.expo.dev/workflow/development-mode/}
 */
const checkIfUpdatesDisabled: Guard = () => {
  const isUsingDevMode = __DEV__;
  const isWebPlatform = Platform.OS === 'web';

  return isUsingDevMode || isWebPlatform;
};

/**
 * Checks for the availability of an initial update.
 */
const checkIfNoInitialUpdateAvailable: Guard<{ result: InitialUpdateCheckResult }> = (
  _args,
  params,
) => {
  // @ts-expect-error UpdateEventType was removed in newer expo-updates; this guard may need a rewrite
  return params.result?.type !== Updates.UpdateEventType.UPDATE_AVAILABLE;
};

/**
 * Checks for the availability of an (non-initial) update.
 */
const checkIfNoNewUpdateIsAvailable: Guard<{ result: UpdateFetchResult | undefined }> = (
  _args,
  params,
) => {
  return !params.result?.isNew;
};

// ─────────────────────────────────────────────────────────────────────────────

export const Guards = {
  checkIfAutoUpdateEnabled,
  checkIfUpdatesDisabled,
  checkIfNoInitialUpdateAvailable,
  checkIfNoNewUpdateIsAvailable,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type InitialUpdateCheckResult = Awaited<ReturnType<typeof checkForInitialUpdate>>;
